/**
 * ScrollableViewport — pure, renderer-agnostic 1-D pan math for a wider/taller-than-viewport
 * background (panorama today; bookpage later, per `canvas-panorama-slides/design.md` D5).
 *
 * It owns the offset arithmetic shared across renderers: bounds, clamping, the
 * `"left top width height"` viewBox string, and the wheel / drag deltas. It holds NO mutable
 * state — the live offset is the synced `<layer>SlideViewBox` field, passed in and returned — and
 * has no fabric/Angular dependency, so it is trivially unit-testable.
 *
 * Construct from the content extent + viewport; the caller sources the extent per slide type
 * (panorama → `slide.panorama` dims; bookpage → fixed `BOOKPAGE_SIZE`). The content is assumed
 * scaled uniformly so its non-scroll axis matches the viewport (the background `contain-*` fit),
 * which is what makes the offset and the viewBox share one coordinate space.
 */
export type ScrollDirection = 'Horizontal' | 'Vertical';

export interface ViewportSize {
    width: number;
    height: number;
}

/** Wheel-to-pan multiplier — parity with the SVG renderer's panorama/bookpage scroll speed. */
const SCROLL_SPEED = 2;
/** deltaMode 1 (lines, e.g. Firefox) → pixels conversion, matching the SVG renderer. */
const LINE_DELTA_PX = 34;

export class ScrollableViewport {
    constructor(
        readonly direction: ScrollDirection,
        private readonly content: ViewportSize,
        private readonly viewport: ViewportSize,
    ) {}

    /**
     * Maximum pan offset along the scroll axis, with the content scaled to fit the other axis.
     * Never negative (content not exceeding the viewport yields 0).
     */
    maxOffset(): number {
        const raw =
            this.direction === 'Vertical'
                ? this.content.height * (this.viewport.width / this.content.width) - this.viewport.height
                : this.content.width * (this.viewport.height / this.content.height) - this.viewport.width;
        return Math.max(0, raw);
    }

    /** Clamp an offset to `[0, maxOffset()]`. */
    clamp(offset: number): number {
        return Math.max(0, Math.min(offset, this.maxOffset()));
    }

    /**
     * Read the scroll-axis offset from a viewBox string (`"left top width height"`):
     * the `left` part for horizontal, the `top` part for vertical. 0 if absent or garbled.
     */
    parseOffset(viewBox: string | undefined): number {
        if (!viewBox) return 0;
        const parts = viewBox.split(/\s+/).map(Number);
        const value = this.direction === 'Vertical' ? parts[1] : parts[0];
        return Number.isFinite(value) ? value : 0;
    }

    /** Format the viewBox string for an offset (clamped defensively). */
    formatViewBox(offset: number): string {
        const o = this.clamp(offset);
        return this.direction === 'Vertical'
            ? `0 ${o} ${this.viewport.width} ${this.viewport.height}`
            : `${o} 0 ${this.viewport.width} ${this.viewport.height}`;
    }

    /** New clamped offset after a wheel tick (deltaMode 1 = lines → pixels). */
    applyWheel(offset: number, deltaY: number, deltaMode: number): number {
        const delta = (deltaMode === 1 ? deltaY * LINE_DELTA_PX : deltaY) * SCROLL_SPEED;
        return this.clamp(offset + delta);
    }

    /**
     * New clamped offset for a grab-drag from `startCoord` to `currentCoord` (pan-independent
     * viewport units). Dragging toward higher coordinates decreases the offset (grab-and-drag).
     */
    applyDrag(startOffset: number, startCoord: number, currentCoord: number): number {
        return this.clamp(startOffset - (currentCoord - startCoord));
    }
}
